import { prefs } from './storage.js';

export const VOICE_NAMES = ['alloy', 'ash', 'ballad', 'coral', 'echo', 'fable', 'nova', 'onyx', 'sage', 'shimmer'] as const;
export type VoiceName = (typeof VOICE_NAMES)[number];

export const VOICE_SPEEDS = [0.8, 1, 1.2, 1.5] as const;
export const VOICE_TONES = ['neutral', 'warm', 'cheerful', 'calm'] as const;
export type VoiceTone = (typeof VOICE_TONES)[number];
export const VOICE_REACTIONS = ['quick', 'normal', 'calm'] as const;
export type VoiceReaction = (typeof VOICE_REACTIONS)[number];
export const VOICE_TALK_MODES = ['auto', 'push'] as const;
export type VoiceTalkMode = (typeof VOICE_TALK_MODES)[number];

export interface VoiceSettings {
  voice: VoiceName;
  /** Playback speed (the voice model itself has no speed control). */
  speed: number;
  tone: VoiceTone;
  /** `fast` picks a quick, cheap chat model for the call; `chat` keeps the one chosen in the composer. */
  model: 'fast' | 'chat';
  /** `auto` detects pauses by itself; `push` records only while the circle is held. */
  talkMode: VoiceTalkMode;
  /** How long a pause ends the phrase. */
  reaction: VoiceReaction;
  /** Talking over the assistant interrupts it (best with headphones). */
  bargeIn: boolean;
  /** Soft sound while the assistant is thinking. */
  cues: boolean;
  webSearch: boolean;
  /** `high` uses the newer speech recogniser when the server has it. */
  recognition: 'standard' | 'high';
}

export const DEFAULT_VOICE_SETTINGS: VoiceSettings = {
  voice: 'alloy',
  speed: 1,
  tone: 'neutral',
  model: 'fast',
  talkMode: 'auto',
  reaction: 'normal',
  bargeIn: false,
  cues: true,
  webSearch: false,
  recognition: 'standard',
};

/** Pause that ends a phrase, per reaction setting. */
export const REACTION_SILENCE_MS: Record<VoiceReaction, number> = { quick: 700, normal: 1100, calm: 1600 };

const STORAGE_KEY = 'voiceSettings.v1';

function oneOf<T extends string>(value: unknown, allowed: readonly T[], fallback: T): T {
  return typeof value === 'string' && (allowed as readonly string[]).includes(value) ? (value as T) : fallback;
}

/** Anything read from storage is untrusted: keep valid fields, reset the rest to defaults. */
export function sanitizeVoiceSettings(raw: unknown): VoiceSettings {
  const d = DEFAULT_VOICE_SETTINGS;
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return { ...d };
  const r = raw as Record<string, unknown>;
  const speed = typeof r.speed === 'number' && Number.isFinite(r.speed) ? Math.min(1.6, Math.max(0.7, r.speed)) : d.speed;
  return {
    voice: oneOf(r.voice, VOICE_NAMES, d.voice),
    speed,
    tone: oneOf(r.tone, VOICE_TONES, d.tone),
    model: oneOf(r.model, ['fast', 'chat'] as const, d.model),
    talkMode: oneOf(r.talkMode, VOICE_TALK_MODES, d.talkMode),
    reaction: oneOf(r.reaction, VOICE_REACTIONS, d.reaction),
    bargeIn: typeof r.bargeIn === 'boolean' ? r.bargeIn : d.bargeIn,
    cues: typeof r.cues === 'boolean' ? r.cues : d.cues,
    webSearch: typeof r.webSearch === 'boolean' ? r.webSearch : d.webSearch,
    recognition: oneOf(r.recognition, ['standard', 'high'] as const, d.recognition),
  };
}

type Store = Pick<typeof prefs, 'get' | 'set'>;

export function loadVoiceSettings(store: Store = prefs): VoiceSettings {
  try {
    const raw = store.get(STORAGE_KEY);
    return sanitizeVoiceSettings(raw ? JSON.parse(raw) : null);
  } catch {
    return { ...DEFAULT_VOICE_SETTINGS };
  }
}

export function saveVoiceSettings(settings: VoiceSettings, store: Store = prefs): void {
  try {
    store.set(STORAGE_KEY, JSON.stringify(settings));
  } catch {
    /* settings simply do not persist */
  }
}
